import type { FC, PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useBodyScrollLock } from "@ui/hooks/useBodyScrollLock";
import { useFocusTrap } from "@ui/hooks/useFocusTrap";
import classNames from "classnames";

import { Container } from "@jti/core-components/components/Container";

import { DialogCloseButton } from "./DialogCloseButton";

import styles from "./Dialog.module.scss";

export type DialogProps = {
  externalContent?: ReactNode;
  isOpened?: boolean;
  theme?: "dark" | "light";
  fullscreen?: boolean;
  closable?: boolean;
  dialogClassName?: string;
  className?: string;
  wrapperClassName?: string;
  closeStyle?: string;
  closeWrapperClassName?: string;
  onClose?(): void;
  dataTestId?: string;
  portalDomNode?: HTMLElement;
  backgroundColor?: string;
  backgroundBlur?: number;
  noPadding?: boolean;
  id?: string;
  isUnwrapped?: boolean;
  isBodyScrollDisabled?: boolean;
  noContent?: boolean;
  ariaLabelledBy?: string;
  ariaLabel?: string;
  role?: "dialog" | "alertdialog";
  contentWrapperClassName?: string;
  withContentPadding?: boolean;
};

export const BACKGROUND_OPTIONS = {
  blurredWhite: {
    color: "rgba(255, 255, 255, 0.50)",
    blur: 5,
  },
  blurredBlack: {
    color: "rgba(0, 0, 0, 0.50)",
    blur: 5,
  },
  blurredSoftBlack: {
    color: "rgba(45, 41, 38, 0.50)",
    blur: 5,
  },
  white: {
    color: "rgba(255, 255, 255, 1)",
    blur: 0,
  },
  black: {
    color: "rgba(0, 0, 0, 1)",
    blur: 0,
  },
  softBlack: {
    color: "rgba(45, 41, 38, 1)",
    blur: 0,
  },
  transparent: {
    color: "transparent",
    blur: 5,
  },
} as const;

export const Dialog: FC<PropsWithChildren<DialogProps>> = ({
  isOpened = true,
  children,
  externalContent,
  theme = "light",
  fullscreen = false,
  closable = true,
  onClose,
  className = "",
  wrapperClassName = "",
  dialogClassName = "",
  closeStyle = "",
  closeWrapperClassName = "",
  dataTestId = "dialog",
  portalDomNode,
  backgroundColor = BACKGROUND_OPTIONS.transparent.color,
  backgroundBlur = BACKGROUND_OPTIONS.transparent.blur,
  noPadding = false,
  id,
  isUnwrapped,
  isBodyScrollDisabled,
  noContent = false,
  ariaLabelledBy,
  ariaLabel = "dialog",
  role = "dialog",
  contentWrapperClassName,
  withContentPadding = true,
}) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const { toggleScroll } = useBodyScrollLock();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useFocusTrap({ isActive: !isUnwrapped && isOpened, containerRef: dialogRef, mountedSignal: mounted });

  useEffect(() => {
    if (!isBodyScrollDisabled) {
      toggleScroll(isOpened);
    }
    setMounted(true);
  }, [isOpened, isBodyScrollDisabled, toggleScroll, setMounted]);

  useEffect(() => {
    if (isUnwrapped) return;

    const dialogElement = dialogRef.current;
    if (!dialogElement) return;

    if (isOpened) {
      if (!dialogElement.open) {
        dialogElement.show();
        dialogElement.focus();
      }

      return;
    }

    if (dialogElement.open) {
      dialogElement.close();
    }
  }, [isOpened, isUnwrapped, mounted]);

  useEffect(() => {
    if (isUnwrapped || !isOpened || !closable) return;

    const dialogElement = dialogRef.current;
    if (!dialogElement) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      onClose?.();
    };

    dialogElement.addEventListener("keydown", handleKeyDown);

    return () => {
      dialogElement.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpened, isUnwrapped, closable, onClose, mounted]);

  const containerStyle = {
    "--background-color": backgroundColor,
    "--backdrop-filter": `blur(${backgroundBlur}px)`,
  };

  const content = (
    <div
      className={classNames(styles.dialogWrapper, wrapperClassName, {
        [styles.unwrapped]: isUnwrapped,
        [styles.isShown]: isOpened,
        [styles.isFullscreen]: fullscreen,
      })}
    >
      {externalContent && <div className={styles.externalContent}>{externalContent}</div>}
      <div
        id={id}
        className={classNames(styles.container, dialogClassName, {
          ["light-theme"]: theme === "light",
          ["dark-theme"]: theme === "dark",
          [styles.isContainerFullscreen]: fullscreen,
        })}
        style={containerStyle}
      >
        <Container className={classNames(styles.contentWrapper, { [styles.dialogFullscreen]: fullscreen })}>
          <dialog
            role={role}
            ref={dialogRef}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-modal="true"
            data-testid={dataTestId}
            className={classNames(styles.dialog, className, {
              [styles.dialogFullscreen]: fullscreen,
              [styles.noPadding]: noPadding,
              [styles.noContent]: noContent,
            })}
          >
            {closable && (
              <DialogCloseButton
                onClick={onClose}
                buttonClassName={closeStyle}
                containerClassName={closeWrapperClassName}
              />
            )}
            <div
              className={classNames(
                {
                  [styles.withContentPadding]: withContentPadding,
                },
                contentWrapperClassName
              )}
            >
              {children}
            </div>
          </dialog>
        </Container>
      </div>
    </div>
  );

  // This prop should be only used for CMS authoring purposes
  if (isUnwrapped) {
    return content;
  }

  return mounted ? createPortal(content, portalDomNode || document.body) : null;
};
